import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Mail, Search, Download, Filter, Home, MessageSquare,
  LogOut, Check, Clock, Edit, Trash2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_4f8d1127-7831-43ae-8d3d-944f658ba2e9/artifacts/6bknvtgo_logo.png';

export const AdminSubscribers = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchSubscribers();
  }, [search, statusFilter]);

  const fetchSubscribers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/subscribers?${params}`);
      setSubscribers(response.data);
    } catch (error) {
      toast.error('Failed to fetch subscribers');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkContacted = async (subscriber) => {
    try {
      await axios.put(`${API}/subscribers/${subscriber.id}`, {
        status: 'contacted',
        last_contacted_date: new Date().toISOString()
      });
      toast.success('Marked as contacted');
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to update subscriber');
    }
  };

  const handleUpdateNotes = async () => {
    if (!selectedSubscriber) return;
    
    try {
      await axios.put(`${API}/subscribers/${selectedSubscriber.id}`, {
        notes: notes
      });
      toast.success('Notes updated');
      setSelectedSubscriber(null);
      setNotes('');
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to update notes');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;
    
    try {
      await axios.delete(`${API}/subscribers/${id}`);
      toast.success('Subscriber deleted');
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to delete subscriber');
    }
  };

  const handleExport = () => {
    window.open(`${API}/subscribers/export/csv`, '_blank');
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const contactedCount = subscribers.filter(s => s.last_contacted_date).length;
  const pendingCount = subscribers.length - contactedCount;

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900" data-testid="admin-subscribers">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 w-64 h-full bg-slate-900 text-white z-40">
        <div className="p-6 border-b border-slate-800">
          <img src={LOGO_URL} alt="RENVITEK" className="h-10" />
          <p className="text-xs text-slate-400 mt-2">Admin Panel</p>
        </div>
        
        <nav className="p-4 space-y-2">
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-800 text-slate-300 transition-colors"
          >
            <Home size={20} />
            Dashboard
          </Link>
          <Link
            to="/admin/subscribers"
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-emerald-600 text-white"
          >
            <Mail size={20} />
            Subscribers
          </Link>
          <Link
            to="/admin/contacts"
            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-800 text-slate-300 transition-colors"
          >
            <MessageSquare size={20} />
            Contacts
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
              {user?.username?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div>
              <p className="text-sm font-medium">{user?.username || 'Admin'}</p>
              <p className="text-xs text-slate-400">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
              Subscribers
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Manage newsletter subscribers
            </p>
          </div>
          <Button
            onClick={handleExport}
            className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
            data-testid="export-csv-btn"
          >
            <Download size={18} />
            Export CSV
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm">
            <p className="text-sm text-slate-500">Total Subscribers</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">{subscribers.length}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm">
            <p className="text-sm text-slate-500">Contacted</p>
            <p className="text-3xl font-bold text-emerald-500">{contactedCount}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm">
            <p className="text-sm text-slate-500">Pending</p>
            <p className="text-3xl font-bold text-amber-500">{pendingCount}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 mb-6 flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <Input
              type="text"
              placeholder="Search by email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2 bg-white dark:bg-slate-800"
              data-testid="status-filter"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="contacted">Contacted</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-700">
                <TableHead>Email</TableHead>
                <TableHead>Date Subscribed</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Contacted</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="loading-spinner mx-auto" />
                  </TableCell>
                </TableRow>
              ) : subscribers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No subscribers found
                  </TableCell>
                </TableRow>
              ) : (
                subscribers.map((subscriber) => (
                  <TableRow key={subscriber.id} data-testid={`subscriber-row-${subscriber.id}`}>
                    <TableCell className="font-medium">{subscriber.email}</TableCell>
                    <TableCell>{new Date(subscriber.date_subscribed).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <span className={`status-badge ${subscriber.status === 'active' ? 'status-active' : subscriber.status === 'contacted' ? 'status-contacted' : 'status-inactive'}`}>
                        {subscriber.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      {subscriber.last_contacted_date 
                        ? new Date(subscriber.last_contacted_date).toLocaleDateString()
                        : <span className="text-slate-400">Not contacted</span>
                      }
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {subscriber.notes || <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkContacted(subscriber)}
                          className="text-emerald-600 hover:bg-emerald-50"
                          data-testid={`mark-contacted-btn-${subscriber.id}`}
                        >
                          <Check size={16} />
                        </Button>
                        
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedSubscriber(subscriber);
                                setNotes(subscriber.notes || '');
                              }}
                            >
                              <Edit size={16} />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Edit Notes</DialogTitle>
                              <p className="text-sm text-slate-500">
                                Add notes for {selectedSubscriber?.email}
                              </p>
                            </DialogHeader>
                            <Textarea
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              placeholder="Add notes..."
                              rows={4}
                            />
                            <DialogFooter>
                              <Button onClick={handleUpdateNotes} className="bg-emerald-600 hover:bg-emerald-700">
                                Save Notes
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(subscriber.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
};

export default AdminSubscribers;
